import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import GenericDatePicker from "../GenericDatePicker";
import styles from "./styles.module.css";

/**
 * ModalFiltrosComplexos - Componente genérico para filtros complexos
 *
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Função para fechar o modal
 * @param {Array} props.columns - Array de colunas disponíveis para filtro
 * @param {Function} props.onApplyFilters - Função chamada ao aplicar filtros
 * @param {string} props.title - Título do modal (opcional)
 * @param {string} props.context - Contexto do uso (ex: "PIS-COFINS", "ICMS", "Jurídico")
 * @param {Array} props.initialFilters - Filtros iniciais (opcional)
 * @param {Object} props.customOperators - Operadores customizados por tipo (opcional)
 */
const ModalFiltrosComplexos = ({
  open,
  onClose,
  columns = [],
  onApplyFilters,
  title = "Filtros Avançados",
  context = "Sistema",
  initialFilters = [],
  customOperators = {},
}) => {
  const [filtros, setFiltros] = useState(initialFilters);

  // Operadores disponíveis baseados no tipo da coluna
  const getOperadores = (tipo) => {
    // Se há operadores customizados para este tipo, usar eles
    if (customOperators[tipo]) {
      return customOperators[tipo];
    }

    // Operadores padrão baseados no tipo
    switch (tipo) {
      case "date":
        return [
          { value: "=", label: "Igual a" },
          { value: ">", label: "Depois de" },
          { value: "<", label: "Antes de" },
          { value: ">=", label: "A partir de" },
          { value: "<=", label: "Até" },
          { value: "between", label: "Entre" },
        ];
      case "number":
      case "monetary":
      case "percent":
        return [
          { value: "=", label: "Igual a" },
          { value: ">", label: "Maior que" },
          { value: "<", label: "Menor que" },
          { value: ">=", label: "Maior ou igual" },
          { value: "<=", label: "Menor ou igual" },
          { value: "between", label: "Entre" },
        ];
      case "string":
      case "cnpj":
        return [
          { value: "contains", label: "Contém" },
          { value: "startsWith", label: "Começa com" },
          { value: "endsWith", label: "Termina com" },
          { value: "=", label: "Igual a" },
          { value: "!=", label: "Diferente de" },
        ];
      case "boolean":
        return [
          { value: "=", label: "Igual a" },
          { value: "!=", label: "Diferente de" },
        ];
      default:
        return [
          { value: "=", label: "Igual a" },
          { value: "!=", label: "Diferente de" },
          { value: "contains", label: "Contém" },
        ];
    }
  };

  // Adicionar novo filtro
  const adicionarFiltro = () => {
    setFiltros([
      ...filtros,
      {
        id: Date.now(),
        coluna: "",
        operador: "",
        valor: "",
        valorFinal: "", // Para operador "between"
      },
    ]);
  };

  // Remover filtro
  const removerFiltro = (id) => {
    setFiltros(filtros.filter((f) => f.id !== id));
  };

  // Atualizar filtro
  const atualizarFiltro = (id, campo, valor) => {
    setFiltros(
      filtros.map((f) => {
        if (f.id === id) {
          const novoFiltro = { ...f, [campo]: valor };

          // Se mudou a coluna, resetar operador e valores
          if (campo === "coluna") {
            novoFiltro.operador = "";
            novoFiltro.valor = "";
            novoFiltro.valorFinal = "";
          }

          return novoFiltro;
        }
        return f;
      })
    );
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    const filtrosValidos = filtros.filter(
      (f) => f.coluna && f.operador && f.valor !== ""
    );

    onApplyFilters(filtrosValidos);
    onClose();
  };

  // Limpar filtros
  const limparFiltros = () => {
    console.log(`🔍 [ModalFiltrosComplexos-${context}] Limpando filtros:`, {
      filtrosAntes: filtros.length,
      action: "CLEAR_FILTERS",
    });

    setFiltros([]);
    onApplyFilters([]);
    // Não fechar o modal - apenas limpar os filtros
  };

  // Renderizar campo de valor baseado no tipo da coluna
  const renderizarCampoValor = (filtro) => {
    const colunaSelecionada = columns.find((c) => c.label === filtro.coluna);
    const tipo = colunaSelecionada?.tipo || "string";

    switch (tipo) {
      case "date":
        return (
          <Grid container spacing={1}>
            <Grid item xs={filtro.operador === "between" ? 6 : 12}>
              <GenericDatePicker
                value={filtro.valor}
                onChange={(date) => atualizarFiltro(filtro.id, "valor", date)}
                label="Data"
                size="small"
                fullWidth
              />
            </Grid>
            {filtro.operador === "between" && (
              <Grid item xs={6}>
                <GenericDatePicker
                  value={filtro.valorFinal}
                  onChange={(date) =>
                    atualizarFiltro(filtro.id, "valorFinal", date)
                  }
                  label="Data Final"
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        );

      case "number":
      case "monetary":
      case "percent":
        return (
          <Grid container spacing={1}>
            <Grid item xs={filtro.operador === "between" ? 6 : 12}>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={filtro.valor}
                onChange={(e) =>
                  atualizarFiltro(filtro.id, "valor", e.target.value)
                }
                label="Valor"
                InputProps={{
                  startAdornment:
                    tipo === "monetary" ? (
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        R$
                      </Typography>
                    ) : tipo === "percent" ? (
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        %
                      </Typography>
                    ) : null,
                }}
              />
            </Grid>
            {filtro.operador === "between" && (
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={filtro.valorFinal}
                  onChange={(e) =>
                    atualizarFiltro(filtro.id, "valorFinal", e.target.value)
                  }
                  label="Valor Final"
                  InputProps={{
                    startAdornment:
                      tipo === "monetary" ? (
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          R$
                        </Typography>
                      ) : tipo === "percent" ? (
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          %
                        </Typography>
                      ) : null,
                  }}
                />
              </Grid>
            )}
          </Grid>
        );

      case "boolean":
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Valor</InputLabel>
            <Select
              value={filtro.valor}
              onChange={(e) =>
                atualizarFiltro(filtro.id, "valor", e.target.value)
              }
              label="Valor"
            >
              <MenuItem value="true">Verdadeiro</MenuItem>
              <MenuItem value="false">Falso</MenuItem>
            </Select>
          </FormControl>
        );

      default:
        return (
          <TextField
            size="small"
            fullWidth
            value={filtro.valor}
            onChange={(e) =>
              atualizarFiltro(filtro.id, "valor", e.target.value)
            }
            label="Valor"
            placeholder="Digite o valor..."
          />
        );
    }
  };

  // Obter operadores disponíveis para o filtro atual
  const operadoresDisponiveis = useMemo(() => {
    if (!filtros.length) return [];

    return filtros.map((filtro) => {
      const colunaSelecionada = columns.find((c) => c.label === filtro.coluna);
      return colunaSelecionada ? getOperadores(colunaSelecionada.tipo) : [];
    });
  }, [filtros, columns, customOperators]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          margin: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle className={styles.modalTitle}>
        <div className={styles.titleContainer}>
          <FilterListIcon sx={{ mr: 1, color: "#18316e" }} />
          <Typography variant="h6" component="div" sx={{ color: "#18316e" }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", ml: 1 }}>
            ({context})
          </Typography>
        </div>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#18316e",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent className={styles.modalContent}>
        <div className={styles.filtrosContainer}>
          {filtros.length === 0 ? (
            <div className={styles.emptyState}>
              <Typography variant="body2" color="text.secondary">
                Nenhum filtro adicionado. Clique em "Adicionar Filtro" para
                começar.
              </Typography>
            </div>
          ) : (
            filtros.map((filtro, index) => (
              <div key={filtro.id} className={styles.filtroRow}>
                <Grid container spacing={1} alignItems="center">
                  {/* Seleção da Coluna */}
                  <Grid item xs={12} sm={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Coluna</InputLabel>
                      <Select
                        value={filtro.coluna}
                        onChange={(e) =>
                          atualizarFiltro(filtro.id, "coluna", e.target.value)
                        }
                        label="Coluna"
                      >
                        {columns.map((col) => (
                          <MenuItem key={col.label} value={col.label}>
                            {col.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Operador */}
                  <Grid item xs={12} sm={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Operador</InputLabel>
                      <Select
                        value={filtro.operador}
                        onChange={(e) =>
                          atualizarFiltro(filtro.id, "operador", e.target.value)
                        }
                        label="Operador"
                        disabled={!filtro.coluna}
                      >
                        {operadoresDisponiveis[index]?.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Campo de Valor */}
                  <Grid item xs={12} sm={4}>
                    {renderizarCampoValor(filtro)}
                  </Grid>

                  {/* Botão Remover */}
                  <Grid item xs={12} sm={1}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removerFiltro(filtro.id)}
                        sx={{
                          color: "#d32f2f",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.04)",
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Grid>
                </Grid>
              </div>
            ))
          )}

          {/* Botão Adicionar Filtro */}
          <div className={styles.addFilterContainer}>
            <Button
              variant="outlined"
              onClick={adicionarFiltro}
              startIcon={<FilterListIcon />}
              size="small"
              sx={{
                borderColor: "#18316e",
                color: "#18316e",
                "&:hover": {
                  borderColor: "#18316e",
                  backgroundColor: "rgba(24, 49, 110, 0.04)",
                },
              }}
            >
              Adicionar Filtro
            </Button>
          </div>
        </div>
      </DialogContent>

      <Divider />

      <DialogActions className={styles.modalActions}>
        <div className={styles.buttonGroupLeft}>
          <Button onClick={onClose} className={styles.btnFechar} size="small">
            Fechar
          </Button>
          <Button
            onClick={limparFiltros}
            className={styles.btnFechar}
            size="small"
          >
            Limpar
          </Button>
        </div>
        <div className={styles.buttonGroupRight}>
          <Button
            onClick={aplicarFiltros}
            variant="contained"
            className={styles.btnAplicar}
            size="small"
            disabled={
              filtros.filter((f) => f.coluna && f.operador && f.valor !== "")
                .length === 0
            }
          >
            Aplicar
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFiltrosComplexos;
